// src/components/ErrorToast.jsx
// Mounted once, globally, in App.jsx (alongside OfflineBanner/AnnouncementBanner).
// Listens on errorToastBus for backend 500s / network failures surfaced by
// kailux.js's axios interceptor and shows a small, dismissible, non-technical
// toast with a reference code — "screenshot this and send it to support".
//
// Deliberately does NOT show 4xx validation errors (those already have their
// own inline handling on each page, e.g. "Client already exists") — only
// infrastructure-level failures the person can't self-serve out of. See
// kailux.js's shouldToastError() for the exact rule.
import { useState, useEffect, useCallback } from 'react'
import { subscribeToErrorToasts } from '../utils/errorToastBus'

import { T, alpha } from '../theme/theme'
const AUTO_DISMISS_MS = 15000

export function ErrorToast() {
  const [toasts, setToasts] = useState([])

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  useEffect(() => {
    const unsubscribe = subscribeToErrorToasts((toast) => {
      setToasts((prev) => {
        // Avoid stacking duplicates of the exact same error_id (e.g. a
        // retried request that fails the same way twice in a row).
        if (toast.errorId && prev.some((t) => t.errorId === toast.errorId)) return prev
        return [...prev, toast]
      })
      setTimeout(() => dismiss(toast.id), AUTO_DISMISS_MS)
    })
    return unsubscribe
  }, [dismiss])

  if (toasts.length === 0) return null

  return (
    <div
      style={{
        position: 'fixed', bottom: '20px', right: '20px', zIndex: 9999,
        display: 'flex', flexDirection: 'column', gap: '10px',
        maxWidth: '360px', width: 'calc(100% - 40px)',
      }}
      role="alert"
      aria-live="assertive"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          style={{
            background: T.s2, color: T.tx, borderRadius: '12px',
            border: `1px solid ${alpha(T.rd, 0.4)}`, borderLeft: `4px solid ${T.rd}`,
            boxShadow: T.shl,
            padding: '14px 16px', fontFamily: "'Outfit',sans-serif",
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
            <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
              <div style={{ fontWeight: 700, marginBottom: '4px' }}>Something went wrong</div>
              <div style={{ color: T.lt }}>{t.message}</div>
              {t.errorId && (
                <div style={{
                  marginTop: '8px', fontFamily: "'Roboto Mono', monospace",
                  fontSize: '12px', color: T.mt, background: alpha(T.tx, 0.06),
                  borderRadius: '6px', padding: '4px 8px', display: 'inline-block',
                }}>
                  Ref: {t.errorId}
                </div>
              )}
              <div style={{ marginTop: '6px', fontSize: '11px', color: T.mt }}>
                If this keeps happening, screenshot this and send it to support.
              </div>
            </div>
            <button
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss"
              style={{
                background: 'none', border: 'none', color: T.mt,
                fontSize: '16px', lineHeight: 1, cursor: 'pointer', padding: 0,
              }}
            >×</button>
          </div>
        </div>
      ))}
    </div>
  )
}

export default ErrorToast
